import { ExternalLinkIcon, SparklesIcon } from "lucide-react";

import { Description } from "@/components/Description";
import { Badge } from "@/components/ui/badge";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Anchor } from "@/components/ui/typography";
import { FeedPulse, PulseCitation } from "@/lib/types";
import { atUriToBskyUrl, cn } from "@/lib/utils";

function pulseDateLabel(pulseDate: string | null) {
  if (!pulseDate) {
    return "";
  }
  const [y = 0, m = 1, d = 1] = pulseDate.split("-").map(Number);
  if (!y) {
    return pulseDate;
  }
  const date = new Date(Date.UTC(y, m - 1, d));
  const today = new Date();
  const yesterday = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 1),
  );
  if (date.getTime() === yesterday.getTime()) {
    return "yesterday";
  }
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/**
 * A day's Feed Pulse: what the feed was about, summarized on a co/core
 * member's Mac from the feed's most-viewed posts, with the provider credit
 * and the signed receipt. Renders on the public feed page and the owner view.
 *
 * 🔴 The link says "signed receipt", NOT "verified receipt", and the tooltip no
 * longer promises anyone can verify it. Both were true claims about the design
 * and false claims about production: measured 2026-09-16, only 5 of 14 stored
 * receipts actually passed dev.cocore.compute.verifyReceipt. The provider signs
 * every receipt, which is what we now say; whether the advisor's
 * countersignature checks out depends on the provider running >= 0.9.55, which
 * a third of the fleet does not. Do not restore the stronger wording without
 * re-measuring.
 *
 * Only semantic radix utilities (bg-slate-subtle, text-slate-dim, ...) so the
 * card reads on the teal marketplace ground and in dark mode; the numbered
 * scales invert between themes.
 */
/**
 * The link behind a citation. Every claim in a pulse names the post it came
 * from, so a reader can check it in one click — the whole point of citing at
 * all. The model never emits a URL (a small model asked for one invents it);
 * the API resolves its index against the posts it was given, so this renders
 * only when there is a real post behind it.
 */
function SourceLink({
  citation,
  inline = false,
  label = "source",
  className,
}: {
  citation: PulseCitation;
  inline?: boolean;
  label?: string;
  className?: string;
}) {
  if (!citation.post_url) {
    return null;
  }
  return (
    <Anchor
      href={citation.post_url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${label} on Bluesky`}
      className={cn(
        "text-slate-dim hover:text-slate-normal whitespace-nowrap",
        inline ? "text-xs align-baseline" : "text-xs flex items-center gap-1",
        className,
      )}
    >
      {inline ? (
        <ExternalLinkIcon className="h-3 w-3 inline" aria-hidden />
      ) : (
        <>
          {label}
          <ExternalLinkIcon className="h-3 w-3" aria-hidden />
        </>
      )}
    </Anchor>
  );
}

export function FeedPulseCard({
  pulse,
  className,
}: {
  pulse: FeedPulse;
  className?: string;
}) {
  const provider = pulse.provider;
  // Defensive on purpose: this renders a payload fetched over HTTP, and the API
  // still serves pulses (and cached payloads) written before these fields
  // existed. A missing key must degrade to a shorter card, never throw — this
  // component took the public feed page down once already.
  const beats = pulse.beats ?? [];
  const themes = pulse.themes ?? [];
  const machine = provider?.machineLabel || "Mac";
  // Say which posts these actually were. `day` is the fallback the worker uses
  // when readers viewed little or nothing that day — it is the posts the feed
  // MATCHED, not the ones that got the most views, and calling them
  // "most-viewed" was wrong. `latest` is retired but historical rows carry it.
  const sample =
    pulse.source_kind === "top_viewed"
      ? `${pulse.source_post_count} most-viewed posts`
      : pulse.source_kind === "day"
        ? `${pulse.source_post_count} posts from that day`
        : `${pulse.source_post_count} recent posts`;

  return (
    <section
      aria-label="Feed Pulse"
      className={cn(
        "border rounded-md border-slate-6 bg-slate-subtle dark:border-slatedark-6 px-4 py-3 md:p-5 flex flex-col gap-3",
        className,
      )}
    >
      <div className="flex items-center gap-1.5 font-semibold text-slate-dim text-xs uppercase tracking-wide">
        <SparklesIcon className="h-3.5 w-3.5" aria-hidden />
        <span>Feed Pulse · {pulseDateLabel(pulse.pulse_date)}</span>
      </div>
      {pulse.headline && (
        <h2 className="text-xl md:text-2xl font-semibold leading-snug text-balance">
          {pulse.headline}
        </h2>
      )}
      {pulse.quote?.text && (
        <blockquote className="border-l-2 border-slate-6 dark:border-slatedark-6 pl-3 md:pl-4 my-0">
          <p className="text-base md:text-lg italic text-slate-normal m-0">
            &ldquo;{pulse.quote.text}&rdquo;
          </p>
          {pulse.quote.post_url && (
            <SourceLink
              citation={pulse.quote}
              className="mt-1"
              label="Read the post"
            />
          )}
        </blockquote>
      )}
      {beats.length > 0 && (
        <ul className="flex flex-col gap-1.5 list-none p-0 m-0">
          {beats.map((beat) => (
            <li
              key={`${beat.index}-${beat.text.slice(0, 24)}`}
              className="text-base text-slate-normal flex gap-2"
            >
              <span aria-hidden className="text-slate-dim select-none">
                &middot;
              </span>
              <span>
                {beat.text} <SourceLink citation={beat} inline />
              </span>
            </li>
          ))}
        </ul>
      )}
      {pulse.tension?.text && (
        <div className="text-base text-slate-normal bg-ambera-3 dark:bg-amberdarka-3 rounded-md px-3 py-2">
          <span className="font-semibold text-slate-dim text-xs uppercase tracking-wide block">
            The open question
          </span>
          {pulse.tension.text} <SourceLink citation={pulse.tension} inline />
        </div>
      )}
      {/* Pulses written before 2026-09-12 carry one prose paragraph whose [n]
          markers point at posts the payload no longer resolves. Shown as-is. */}
      {!beats.length && pulse.summary && (
        <Description className="text-base text-slate-normal">
          {pulse.summary}
        </Description>
      )}
      {themes.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 list-none p-0 m-0">
          {themes.map((theme) => (
            <li key={theme}>
              <Badge variant="outline">{theme}</Badge>
            </li>
          ))}
        </ul>
      )}
      {pulse.standout?.post_uri && (
        <div className="text-sm text-slate-dim">
          Standout post:{" "}
          <Anchor
            href={pulse.standout.post_url || atUriToBskyUrl(pulse.standout.post_uri)}
            target="_blank"
            rel="noopener noreferrer"
          >
            open on Bluesky
          </Anchor>
          {pulse.standout.why ? ` — ${pulse.standout.why}` : ""}
        </div>
      )}
      <div className="text-xs text-slate-dim flex flex-wrap items-center gap-x-1">
        <span>
          Summarized from {sample} on{" "}
          {provider?.handle ? (
            <>
              <SimpleTooltip
                message={
                  provider.displayName
                    ? `${provider.displayName} shares this ${machine} on co/core`
                    : `A co/core member's ${machine}`
                }
              >
                <span className="font-medium text-slate-normal">
                  @{provider.handle}
                </span>
              </SimpleTooltip>
              {"'s "}
              {machine}
            </>
          ) : (
            "a co/core member's Mac"
          )}
        </span>
        {pulse.receipt_url && (
          <>
            <span aria-hidden>·</span>
            <SimpleTooltip message="Every co/core job leaves a signed receipt on the provider's own account: the model, the token counts, and a hash of the input and output.">
              <Anchor
                href={pulse.receipt_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                signed receipt
              </Anchor>
            </SimpleTooltip>
          </>
        )}
      </div>
    </section>
  );
}
